// ===========================================
// COMPONENTE: Dialog para invitar usuario (Multi-Tenant)
// ===========================================

import { useEffect } from 'react'
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Loader2, UserPlus, Send } from 'lucide-react'
import type { RolMultiTenant } from '@/types/roles'
import { ROL_MULTITENANT_CONFIG } from '@/types/roles'
import {
  useUsuarioForm,
  getFieldError,
  type InvitarUsuarioFormValues,
} from './forms'
import { useStore } from '@tanstack/react-form'
import { FormDialogContent } from '@/components/ui/form-dialog-content'

// Re-export para compatibilidad con callers (barrel usuarios/index.ts + Usuarios.tsx)
export type { InvitarUsuarioFormValues }

interface UsuarioFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: InvitarUsuarioFormValues) => Promise<void>
  isLoading?: boolean
}

// Workspaces disponibles.
// Mono-sector: por ahora solo Contable. Otros sectores (jurídico, laboral, etc.) son futuro.
const WORKSPACES_DISPONIBLES = [
  { id: 'contable', label: 'Contable' },
]

export function UsuarioFormDialog({
  open,
  onOpenChange,
  onSubmit,
  isLoading,
}: UsuarioFormDialogProps) {
  const { form } = useUsuarioForm({ onSubmit })
  const isDirty = useStore(form.store, (s) => s.isDirty)

  // Resetear formulario cada vez que se abre el dialog
  useEffect(() => {
    if (open) form.reset()
  }, [open, form])

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.handleSubmit()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <FormDialogContent
        isDirty={isDirty}
        onDiscard={() => onOpenChange(false)}
        className="sm:max-w-[500px]"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Invitar Usuario
          </DialogTitle>
          <DialogDescription>
            Envía una invitación por email para que el usuario configure su cuenta.
            Recibirá un link para establecer su contraseña.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleFormSubmit} className="space-y-4">
          <form.Field name="email">
            {(field) => (
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  placeholder="usuario@ejemplo.com"
                  value={field.state.value ?? ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                <p className="text-sm text-muted-foreground">
                  El usuario recibirá un email para configurar su cuenta
                </p>
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {getFieldError(field.state.meta.errors)}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="nombre">
            {(field) => (
              <div className="space-y-2">
                <Label>Nombre</Label>
                <Input
                  placeholder="Nombre completo"
                  value={field.state.value ?? ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="rol">
            {(field) => (
              <div className="space-y-2">
                <Label>Rol *</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(value) =>
                    field.handleChange(value as RolMultiTenant)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona un rol" />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(ROL_MULTITENANT_CONFIG) as RolMultiTenant[]).map(
                      (rol) => (
                        <SelectItem key={rol} value={rol}>
                          {ROL_MULTITENANT_CONFIG[rol].label}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
                {field.state.value && (
                  <p className="text-sm text-muted-foreground">
                    {ROL_MULTITENANT_CONFIG[field.state.value as RolMultiTenant].description}
                  </p>
                )}
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {getFieldError(field.state.meta.errors)}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="workspaces">
            {(field) => (
              <div className="space-y-2">
                <div className="mb-2">
                  <Label className="text-base">Workspaces *</Label>
                  <p className="text-sm text-muted-foreground">
                    Selecciona los workspaces a los que tendrá acceso
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {WORKSPACES_DISPONIBLES.map((workspace) => {
                    const checked = field.state.value?.includes(workspace.id)
                    return (
                      <div
                        key={workspace.id}
                        className="flex flex-row items-start space-x-3 space-y-0"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(value) =>
                            field.handleChange(
                              value
                                ? [...field.state.value, workspace.id]
                                : field.state.value.filter(
                                    (id) => id !== workspace.id,
                                  ),
                            )
                          }
                        />
                        <Label className="font-normal cursor-pointer">
                          {workspace.label}
                        </Label>
                      </div>
                    )
                  })}
                </div>
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {getFieldError(field.state.meta.errors)}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Enviar invitación
            </Button>
          </div>
        </form>
      </FormDialogContent>
    </Dialog>
  )
}
